import { useCallback, useMemo, useRef } from "react";
import {
  useDataProvider,
  useGetIdentity,
  type DataProvider,
  type Identifier,
} from "ra-core";

import { matchLeadSource } from "../deals/leadSourceRules";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { LabeledValue, LeadSource, LeadType, Project } from "../types";
import { useCompanyResolver } from "./useCompanyResolver";
import { createEachRow } from "./createEachRow";
import { toConfiguredValue, toInteger, toIsoDate, toText } from "./parseCell";
import type { ImportCell, ImportRow, ProcessImportBatch } from "./types";

/**
 * Lead origin labels (Almikia Epic E2), matched by value or Spanish label the
 * same way every other configured column is (`toConfiguredValue`). This is
 * not a `<CRM>` prop like `dealStages`/`dealCategories`: `LEAD_TYPES` (`../types`)
 * is a fixed enum shared with TASK-008's DB constraint, not a tenant-configurable list.
 */
const LEAD_TYPE_OPTIONS: LabeledValue[] = [
  { value: "bpo", label: "BPO" },
  { value: "proactivo", label: "Proactivo" },
  { value: "almikia", label: "Almikia" },
  { value: "colega", label: "Colega" },
];

/**
 * Score cell as a whole number within the DB's 0-8 range, or undefined for
 * anything else — the same "leave it empty" fallback an unknown category gets.
 */
const toScore = (cell: ImportCell): number | undefined => {
  const score = toInteger(cell);
  return score !== undefined && score >= 0 && score <= 8 ? score : undefined;
};

/** One CSV row, with the values needed before its deal can be created. */
type DealRow = {
  row: ImportRow;
  companyName: string | undefined;
  projectName: string | undefined;
  stage: string | undefined;
};

/**
 * Creates a deal per CSV row. Unknown columns are ignored, missing ones are
 * left empty — except `name`, which the database requires. The `company` column
 * holds a company name: matching companies are reused, unknown ones are created.
 * The `project` column holds a project code or name (Almikia Epic E2): unlike
 * companies, an unmatched project is left empty rather than created — the
 * catalogue (TASK-001) is managed on its own screens.
 */
export function useDealImport(): ProcessImportBatch {
  const { dealCategories, dealStages } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const getCompanies = useCompanyResolver();
  const getProjects = useProjectResolver();
  const getSources = useLeadSourcesCache();

  return useCallback(
    async (batch, onUnmatchedSources) => {
      // Parse the company name, project name and the stage once per row: all
      // are needed to resolve lookups and column indexes up front, then again
      // per deal.
      const rows: DealRow[] = batch.map((row) => ({
        row,
        companyName: toText(row.company),
        projectName: toText(row.project),
        // stage is required, so fall back to the first configured one
        stage: toConfiguredValue(row.stage, dealStages) ?? dealStages[0]?.value,
      }));

      const [companies, projects, indexes] = await Promise.all([
        getCompanies(
          rows
            .map(({ companyName }) => companyName)
            .filter((name): name is string => name !== undefined),
        ),
        getProjects(
          rows
            .map(({ projectName }) => projectName)
            .filter((name): name is string => name !== undefined),
        ),
        appendedIndexes(rows, dataProvider),
      ]);

      // Almikia Epic E2b (TASK-023): the `source` text is matched to an
      // ACTIVE source tag; only direccion creates sources, so an unmatched
      // value stays in the legacy `source` column and is reported.
      const sources = rows.some(({ row }) => toText(row.source) !== undefined)
        ? await getSources()
        : [];
      const matchedSources = rows.map(({ row }) =>
        matchLeadSource(sources, toText(row.source)),
      );
      const unmatchedCount = rows.filter(
        ({ row }, position) =>
          toText(row.source) !== undefined && !matchedSources[position],
      ).length;
      if (unmatchedCount > 0) onUnmatchedSources?.(unmatchedCount);

      const now = new Date().toISOString();
      return createEachRow(
        rows.map(({ row, companyName, projectName, stage }, position) =>
          dataProvider.create("deals", {
            data: {
              name: toText(row.name),
              company_id: companyName
                ? companies.get(companyName)?.id
                : undefined,
              project_id: projectName ? projects.get(projectName) : undefined,
              contact_ids: [],
              category: toConfiguredValue(row.category, dealCategories),
              stage,
              description: toText(row.description),
              // amount lands in a bigint column, which rejects "4500.50"
              amount: toInteger(row.amount),
              expected_closing_date: toIsoDate(row.expected_closing_date),
              lead_type: toConfiguredValue(row.lead_type, LEAD_TYPE_OPTIONS) as
                | LeadType
                | undefined,
              external_broker: toText(row.external_broker),
              lead_source_id: matchedSources[position]?.id,
              // Kept as legacy text only when no active tag matched.
              source: matchedSources[position] ? undefined : toText(row.source),
              score: toScore(row.score),
              posi_intent: toText(row.posi_intent),
              posi_budget: toText(row.posi_budget),
              posi_payment_method: toText(row.posi_payment_method),
              posi_decision_maker: toText(row.posi_decision_maker),
              sales_id: identity?.id,
              index: indexes.get(row) ?? 0,
              created_at: now,
              updated_at: now,
            },
          }),
        ),
      );
    },
    [
      dataProvider,
      dealCategories,
      dealStages,
      getCompanies,
      getProjects,
      getSources,
      identity?.id,
    ],
  );
}

/**
 * Every lead source (the catalogue is small and readable by all roles),
 * fetched once per data provider and reused by every batch of the import.
 */
function useLeadSourcesCache() {
  const dataProvider = useDataProvider();
  const cache = useRef<{ provider: DataProvider; sources: LeadSource[] }>(null);

  return useCallback(async (): Promise<LeadSource[]> => {
    if (cache.current?.provider !== dataProvider) {
      const { data } = await dataProvider.getList<LeadSource>("lead_sources", {
        filter: {},
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
      });
      cache.current = { provider: dataProvider, sources: data };
    }
    return cache.current.sources;
  }, [dataProvider]);
}

/**
 * Resolves the `project` column by code (e.g. "BEA") or name, case-insensitive,
 * to a `project_id` — one cached lookup for the whole import, not one request
 * per row. Unlike `useCompanyResolver`, an unmatched name resolves to
 * undefined instead of creating a project: the catalogue (TASK-001) is closed
 * to the importer, and the sellable-project guard (TASK-008/TASK-010) would
 * reject a fabricated one anyway.
 */
function useProjectResolver() {
  const dataProvider = useDataProvider();
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  const projectsCache = useMemo(
    () => new Map<string, Identifier>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataProvider],
  );

  return useCallback(
    async (names: string[]): Promise<Map<string, Identifier>> => {
      if (names.length > 0 && projectsCache.size === 0) {
        const { data: projects } = await dataProvider.getList<Project>(
          "projects",
          {
            filter: {},
            pagination: { page: 1, perPage: 1000 },
            sort: { field: "id", order: "ASC" },
          },
        );
        for (const project of projects) {
          projectsCache.set(project.code.toLowerCase(), project.id);
          projectsCache.set(project.name.toLowerCase(), project.id);
        }
      }

      return new Map(
        names
          .map((name): [string, Identifier] | undefined => {
            const id = projectsCache.get(name.toLowerCase());
            return id === undefined ? undefined : [name, id];
          })
          .filter(
            (entry): entry is [string, Identifier] => entry !== undefined,
          ),
      );
    },
    [dataProvider, projectsCache],
  );
}

/**
 * One distinct index per imported deal, appended below the deals already in its
 * stage and keeping the CSV row order.
 *
 * The Kanban board sorts each column on `index`, and its drag handler shifts the
 * indexes of the cards around the drop target. A column whose deals all share
 * `index: 0` therefore cannot be reordered — the drop writes `index: 0` again
 * and the card snaps back — and its order is whatever the backend returns.
 */
const appendedIndexes = async (
  rows: DealRow[],
  dataProvider: DataProvider,
): Promise<Map<ImportRow, number>> => {
  const stages = [
    ...new Set(
      rows
        .map(({ stage }) => stage)
        .filter((stage): stage is string => stage !== undefined),
    ),
  ];
  const lastIndexes = new Map(
    await Promise.all(
      stages.map(
        async (stage) =>
          [stage, await lastIndexOf(stage, dataProvider)] as const,
      ),
    ),
  );

  return new Map(
    stages.flatMap((stage) => {
      const firstIndex = (lastIndexes.get(stage) ?? -1) + 1;
      return rows
        .filter((candidate) => candidate.stage === stage)
        .map(({ row }, position) => [row, firstIndex + position] as const);
    }),
  );
};

/** Highest index currently used in a stage, or -1 when it holds no deal. */
const lastIndexOf = async (stage: string, dataProvider: DataProvider) => {
  const { data } = await dataProvider.getList("deals", {
    filter: { stage },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "index", order: "DESC" },
  });
  return data[0]?.index ?? -1;
};
